import { useState } from "react";
import { Camera, ImageOff, Sparkles } from "lucide-react";
import { Reveal } from "./Reveal";
import { BeforeAfterSlider } from "./BeforeAfterSlider";
import { ResponsiveImage, baseFromSrc } from "./ResponsiveImage";
import { BEFORE_AFTER_CATEGORIES, RECENT_VEHICLE_WRAPS } from "../../data/site";

// Renders a real photo as an optimized responsive image (WebP + fallback, lazy).
const SlideImage = ({ src, alt }) => (
  <ResponsiveImage
    base={baseFromSrc(src)}
    alt={alt}
    width={1600}
    height={1000}
    sizes="(max-width:1024px) 100vw, 50vw"
    className="h-full w-full object-cover object-center"
  />
);

const PlaceholderSide = ({ kind, category }) => (
  <div className={`relative flex h-full w-full items-start justify-center pt-10 ${kind === "before" ? "bg-charcoal" : "bg-obsidian"}`}>
    <div className="sfm-grid-bg absolute inset-0 opacity-30" />
    {kind === "after" && (
      <div
        className="absolute inset-0"
        style={{ background: "radial-gradient(600px circle at 70% 40%, rgba(229,169,60,0.16), transparent 60%)" }}
      />
    )}
    <div className="relative flex flex-col items-center px-6 text-center">
      {kind === "before" ? <ImageOff className="text-white/30" size={34} /> : <Sparkles className="text-amber" size={34} />}
      <p className="mt-3 font-display text-2xl font-extrabold uppercase tracking-tight text-white/90">
        {kind === "before" ? "Before" : "After"}
      </p>
      <p className="mt-1 font-mono text-[11px] uppercase tracking-[0.18em] text-white/45">{category} · Sample placeholder</p>
      <p className="mt-1 font-mono text-[10px] uppercase tracking-widest text-white/25">Real photo coming soon</p>
    </div>
  </div>
);

const PlaceholderComparison = ({ category }) => (
  <>
    {/* Desktop: draggable slider */}
    <div className="hidden sm:block" data-testid="ba-slider-wrapper">
      <BeforeAfterSlider
        ariaLabel={`${category} before and after comparison`}
        before={<PlaceholderSide kind="before" category={category} />}
        after={<PlaceholderSide kind="after" category={category} />}
      />
    </div>
    {/* Mobile fallback: both shown clearly, stacked */}
    <div className="grid gap-4 sm:hidden" data-testid="ba-mobile-fallback">
      <div className="relative aspect-[16/10] overflow-hidden rounded-2xl border border-white/10">
        <PlaceholderSide kind="before" category={category} />
      </div>
      <div className="relative aspect-[16/10] overflow-hidden rounded-2xl border border-white/10">
        <PlaceholderSide kind="after" category={category} />
      </div>
    </div>
    <p className="mt-3 font-mono text-[11px] uppercase tracking-widest text-white/30">
      Placeholder comparison · {category} · real matched pair coming soon
    </p>
  </>
);

const RealComparison = ({ item }) => (
  <div data-testid={`ba-item-${item.id}`} className="rounded-2xl border border-white/10 bg-obsidian p-5 sm:p-6">
    <div className="flex flex-wrap items-baseline justify-between gap-2">
      <h3 className="font-display text-xl font-bold uppercase tracking-tight text-white">{item.title}</h3>
      {item.note && (
        <span className="rounded-full border border-amber/40 px-3 py-1 font-mono text-[10px] uppercase tracking-widest text-amber">
          {item.note}
        </span>
      )}
    </div>
    {item.copy && <p className="mt-2 max-w-2xl text-sm text-white/55">{item.copy}</p>}
    <div className="mt-5">
      {/* Desktop: draggable slider */}
      <div className="hidden sm:block">
        <BeforeAfterSlider
          ariaLabel={`${item.title}: ${item.beforeLabel} versus ${item.afterLabel}`}
          beforeLabel={item.beforeLabel}
          afterLabel={item.afterLabel}
          before={<SlideImage src={item.before} alt={`${item.title} — ${item.beforeLabel}`} />}
          after={<SlideImage src={item.after} alt={`${item.title} — ${item.afterLabel}`} />}
        />
      </div>
      {/* Mobile fallback: both shown clearly, stacked with labels */}
      <div className="grid gap-4 sm:hidden" data-testid={`ba-mobile-fallback-${item.id}`}>
        <figure className="relative m-0 aspect-[16/10] overflow-hidden rounded-xl border border-white/10">
          <SlideImage src={item.before} alt={`${item.title} — ${item.beforeLabel}`} />
          <figcaption className="absolute left-3 top-3 rounded-full bg-void/70 px-3 py-1 font-mono text-[10px] uppercase tracking-widest text-white/80 backdrop-blur">
            {item.beforeLabel}
          </figcaption>
        </figure>
        <figure className="relative m-0 aspect-[16/10] overflow-hidden rounded-xl border border-white/10">
          <SlideImage src={item.after} alt={`${item.title} — ${item.afterLabel}`} />
          <figcaption className="absolute left-3 top-3 rounded-full bg-amber/90 px-3 py-1 font-mono text-[10px] uppercase tracking-widest text-void backdrop-blur">
            {item.afterLabel}
          </figcaption>
        </figure>
      </div>
    </div>
  </div>
);

export const BeforeAfter = () => {
  const [active, setActive] = useState(BEFORE_AFTER_CATEGORIES[0].id);
  const cat = BEFORE_AFTER_CATEGORIES.find((c) => c.id === active);

  return (
    <section id="before-after" className="border-y border-white/10 bg-void py-24" data-testid="before-after-section">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <Reveal>
          <p className="font-mono text-xs uppercase tracking-[0.2em] text-amber">The Transformation</p>
          <h2 className="mt-3 font-display text-3xl font-extrabold uppercase tracking-tight text-white sm:text-4xl lg:text-5xl">
            Before &amp; After
          </h2>
          <p className="mt-5 max-w-2xl text-white/60">
            Drag the slider to compare. These are real projects from our portfolio — labelled accurately as completed
            work or proposed concept designs. We're adding more matched photographs as our clients supply them;
            categories still marked <span className="text-white/85">"Sample"</span> are placeholders until real photos
            are ready, and we never pass off unrelated photos as a false before/after.
          </p>
        </Reveal>

        <div className="mt-10 flex flex-wrap gap-2" role="tablist" aria-label="Before and after categories" data-testid="ba-tabs">
          {BEFORE_AFTER_CATEGORIES.map((c) => (
            <button
              key={c.id}
              role="tab"
              aria-selected={active === c.id}
              data-testid={`ba-tab-${c.id}`}
              onClick={() => setActive(c.id)}
              className={`rounded-full border px-5 py-2 font-mono text-xs uppercase tracking-widest transition-colors ${
                active === c.id ? "border-amber bg-amber text-void" : "border-white/15 text-white/60 hover:border-amber hover:text-amber"
              }`}
            >
              {c.name}
            </button>
          ))}
        </div>

        <div className="mt-8">
          {cat.type === "gallery" ? (
            <Reveal>
              <div data-testid="ba-vehicle-panel" className="rounded-2xl border border-amber/25 bg-obsidian p-6 sm:p-8">
                <div className="flex items-center gap-2">
                  <Camera size={16} className="text-amber" />
                  <h3 className="font-display text-xl font-bold uppercase tracking-tight text-white">Recent Vehicle Wraps</h3>
                </div>
                <p className="mt-2 max-w-2xl text-sm text-white/55">
                  Real, recently completed vehicle wraps — shown as finished work, not as before/after pairs.
                </p>
                <div className="mt-6 grid gap-6 md:grid-cols-2">
                  {RECENT_VEHICLE_WRAPS.map((v) => (
                    <figure key={v.src} className="m-0 overflow-hidden rounded-xl border border-white/10 bg-void">
                      <div className="flex aspect-[16/10] items-center justify-center">
                        <img src={v.src} alt={v.label} loading="lazy" decoding="async" className="h-full w-full object-contain" />
                      </div>
                      <figcaption className="border-t border-white/10 bg-obsidian px-4 py-3 font-mono text-[11px] uppercase tracking-widest text-white/55">
                        {v.label}
                      </figcaption>
                    </figure>
                  ))}
                </div>
              </div>
            </Reveal>
          ) : cat.items ? (
            <div className="space-y-8" data-testid="ba-real-items">
              {cat.items.map((it) => (
                <Reveal key={it.id}>
                  <RealComparison item={it} />
                </Reveal>
              ))}
            </div>
          ) : (
            <Reveal key={cat.id}>
              <PlaceholderComparison category={cat.name} />
            </Reveal>
          )}
        </div>
      </div>
    </section>
  );
};
